import {
  compose, equals, isNil, lte, path, prop, propOr,
} from 'ramda';
import { createGlobalState, useStorage } from '@vueuse/core';
import { nanoid } from 'nanoid/non-secure';
import { isNotNullAndNotEmpty } from '@/utilities';

function useCachedStorage(key) {
  const storageWithExpiry = {
    expiry: null,
    data: null,
  };

  const storage = useStorage(key, storageWithExpiry, localStorage);

  const reset = () => {
    storage.value.data = null;
    storage.value.expiry = null;
  };

  const get = () => {
    // no expiration set or expired, reset data
    const isExpired = lte(Date.parse(storage.value.expiry), Date.now());
    const noExpirationSet = isNil(storage.value.expiry);

    if (noExpirationSet || isExpired) {
      storage.value.data = null;
    }

    return storage;
  };

  const data = () => {
    const store = get();
    return store.value.data;
  };

  const set = (d, expiry = null) => {
    storage.value.data = d;
    storage.value.expiry = expiry;
  };

  const hasData = () => {
    const d = data();
    return !isNil(d);
  };

  return {
    reset,
    get,
    hasData,
    data,
    set,
  };
}

export const dummyUserState = {
  employees: [],
  courses: [],
  role: 'owner',
  isAuthenticated: false,
};
const getRole = prop('role');
const getCompanyPlan = path(['plans', 'company']);
const getUserPlan = path(['plans', 'user']);

export const useGlobalState = createGlobalState(
  () => {
    // state
    const userState = useStorage('userState', dummyUserState, localStorage);

    const currentEmployees = useStorage('currentEmployees', [], localStorage);
    const currentCourses = useCachedStorage('currentCourses');
    const currentTrainingCourses = useCachedStorage('currentTrainingCourses');
    const currentPlans = useCachedStorage('plans');
    const currentSubscription = useCachedStorage('currentSubscription');

    const sessionId = useStorage('sessionId', nanoid(), localStorage);

    const resetUserState = () => {
      userState.value = dummyUserState;
      currentEmployees.value.expiry = null;
      currentEmployees.value.data = [];
      currentCourses.reset();
      currentTrainingCourses.reset();
      currentPlans.reset();
      currentSubscription.reset();
      sessionId.value = nanoid();
    };

    const isAuthenticated = () => propOr(false, 'isAuthenticated', userState.value);
    return {
      userState,
      currentEmployees,
      currentCourses,
      currentTrainingCourses,
      currentPlans,
      currentSubscription,
      sessionId,
      resetUserState,
      isAuthenticated,
    };
  },
);

export const isEmployee = compose(equals('employee'), getRole);
export const isOwner = compose(equals('owner'), getRole);
export const hasCompanyPlan = compose(isNotNullAndNotEmpty, getCompanyPlan);
export const hasUserPlan = compose(isNotNullAndNotEmpty, getUserPlan);
export const hasDemoPlan = compose(
  equals(true),
  prop('isDemo'),
  getUserPlan,
);
